import React from 'react';
import {
    Users,
    UserCheck,
    TrendingUp,
    Calendar,
    DollarSign,
    Activity,
    Award,
    Target,
    Clock,
    FileText,
    AlertCircle
} from 'lucide-react';
import { Card } from '../../components/ui';

const DashboardHr: React.FC = () => {
    const hrStats = [
        {
            name: 'Total Staff',
            value: '45',
            change: '+2%',
            changeType: 'positive',
            icon: Users,
            color: 'bg-gradient-to-r from-cyan-500 to-cyan-600',
            trend: 'up',
        },
        {
            name: 'Active Teachers',
            value: '89',
            change: '+5%',
            changeType: 'positive',
            icon: UserCheck,
            color: 'bg-gradient-to-r from-teal-500 to-teal-600',
            trend: 'up',
        },
        {
            name: 'Pending Leaves',
            value: '12',
            change: '-3',
            changeType: 'negative',
            icon: Calendar,
            color: 'bg-gradient-to-r from-orange-500 to-orange-600',
            trend: 'down',
        },
        {
            name: 'New Hires',
            value: '8',
            change: '+3',
            changeType: 'positive',
            icon: Award,
            color: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
            trend: 'up',
        },
    ];

    const hrMetrics = [
        {
            name: 'Monthly Payroll',
            value: '$125,000',
            change: '+8.2%',
            changeType: 'positive',
            icon: DollarSign,
            trend: 'up',
        },
        {
            name: 'Training Hours',
            value: '240',
            change: '+15%',
            changeType: 'positive',
            icon: Clock,
            trend: 'up',
        },
        {
            name: 'Performance Reviews',
            value: '32',
            change: 'Due',
            changeType: 'neutral',
            icon: FileText,
            trend: 'neutral',
        },
    ];

    const recentHrActivities = [
        {
            id: 1,
            type: 'hire',
            message: 'New teacher Sarah Wilson joined Mathematics department',
            time: '2 hours ago',
        },
        {
            id: 2,
            type: 'leave',
            message: 'John Smith submitted leave request for next week',
            time: '4 hours ago',
        },
        {
            id: 3,
            type: 'review',
            message: 'Performance review completed for Mike Johnson',
            time: '6 hours ago',
        },
        {
            id: 4,
            type: 'training',
            message: 'Staff training session scheduled for Friday',
            time: '1 day ago',
        },
    ];

    const upcomingTasks = [
        { task: 'Monthly payroll processing', due: 'Tomorrow', priority: 'high' },
        { task: 'Staff performance reviews', due: 'Next week', priority: 'medium' },
        { task: 'Recruitment for Science teacher', due: '2 weeks', priority: 'high' },
        { task: 'Training program evaluation', due: '3 weeks', priority: 'low' },
    ];

    return (
        <div className="space-y-6">
            {/* HR Dashboard Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold gradient-text">HR Dashboard</h2>
                    <p className="text-gray-600 text-sm">Human Resources Management Overview</p>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <Users className="h-3 w-3" />
                    <span>HR Management System</span>
                </div>
            </div>

            {/* HR Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {hrStats.map((stat) => (
                    <Card key={stat.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600 mb-1">{stat.name}</p>
                                <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</p>
                                <div className="flex items-center space-x-1">
                                    {stat.trend === 'up' ? (
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    ) : stat.trend === 'down' ? (
                                        <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />
                                    ) : (
                                        <Activity className="h-4 w-4 text-blue-500" />
                                    )}
                                    <p className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                                        stat.changeType === 'negative' ? 'text-red-600' : 'text-blue-600'
                                        }`}>
                                        {stat.change} from last month
                                    </p>
                                </div>
                            </div>
                            <div className={`p-4 rounded-2xl ${stat.color} shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                                <stat.icon className="h-8 w-8 text-white" />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* HR Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {hrMetrics.map((metric) => (
                    <Card key={metric.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600 mb-1">{metric.name}</p>
                                <p className="text-2xl font-bold text-gray-900 mb-2">{metric.value}</p>
                                <div className="flex items-center space-x-1">
                                    {metric.trend === 'up' ? (
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    ) : metric.trend === 'down' ? (
                                        <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />
                                    ) : (
                                        <Activity className="h-4 w-4 text-blue-500" />
                                    )}
                                    <p className={`text-sm font-medium ${metric.changeType === 'positive' ? 'text-green-600' :
                                        metric.changeType === 'negative' ? 'text-red-600' : 'text-blue-600'
                                        }`}>
                                        {metric.change} from last month
                                    </p>
                                </div>
                            </div>
                            <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                                <metric.icon className="h-8 w-8 text-white" />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent HR Activities */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Recent HR Activities</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-orange-100 to-red-100">
                            <Activity className="h-5 w-5 text-orange-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {recentHrActivities.map((activity) => (
                            <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                <div className="flex-shrink-0">
                                    <div className={`w-3 h-3 rounded-full mt-2 ${activity.type === 'hire' ? 'bg-emerald-500' :
                                        activity.type === 'leave' ? 'bg-orange-500' :
                                            activity.type === 'review' ? 'bg-blue-500' : 'bg-purple-500'
                                        }`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-900 font-medium">{activity.message}</p>
                                    <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Upcoming Tasks */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Upcoming Tasks</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                            <Target className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {upcomingTasks.map((task, index) => (
                            <div key={index} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{task.task}</p>
                                    <p className="text-xs text-gray-500">Due: {task.due}</p>
                                </div>
                                <div className={`px-2 py-1 rounded-full text-xs font-medium ${task.priority === 'high' ? 'bg-red-100 text-red-700' :
                                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-green-100 text-green-700'
                                    }`}>
                                    {task.priority}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Quick Actions */}
            <Card glassy={true} className="islamic-pattern">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-emerald-100 to-teal-100">
                        <TrendingUp className="h-5 w-5 text-emerald-600" />
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <a
                        href="/staff/new"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <Users className="h-6 w-6 text-cyan-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Add Staff</p>
                    </a>
                    <a
                        href="/teachers/new"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <UserCheck className="h-6 w-6 text-teal-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Add Teacher</p>
                    </a>
                    <a
                        href="/leaves"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <Calendar className="h-6 w-6 text-orange-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Manage Leaves</p>
                    </a>
                    <a
                        href="/reports/hr"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <FileText className="h-6 w-6 text-purple-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">HR Reports</p>
                    </a>
                </div>
            </Card>
        </div>
    );
};

export default DashboardHr;
